/**
 * API endpoints for Calendar Event Files/Media Management
 */

import { ApiService } from '../apiService'
import apiClient from '../client'
import {
  CalendarEventMediaFileDto,
  CalendarEventMediaFileDetailDto,
  CalendarEventMediaUploadResultDto,
  CalendarEventStorageQuotaDto,
  CalendarEventFileSelectionResultDto,
  GetCalendarEventFilesParams,
  UpdateCalendarEventMediaFileRequest,
  SetCalendarEventCoverImageRequest,
  CalendarEventFileListResponse,
  CalendarEventFileUploadOptions,
} from '../types/calendarEventFiles'

export const calendarEventFilesApi = {
  /**
   * Get list of files for an event with filtering and pagination
   */
  getFiles: (eventId: string, params?: GetCalendarEventFilesParams) =>
    ApiService.get<CalendarEventFileListResponse>(`/api/events/${eventId}/files`, params),

  /**
   * Get detailed information about a specific file
   */
  getFile: (eventId: string, fileId: string) =>
    ApiService.get<CalendarEventMediaFileDetailDto>(`/api/events/${eventId}/files/${fileId}`),

  /**
   * Get storage quota for an event
   */
  getStorageQuota: (eventId: string) =>
    ApiService.get<CalendarEventStorageQuotaDto>(`/api/events/${eventId}/files/quota`),

  /**
   * Upload a new file to the event
   */
  uploadFile: (eventId: string, file: File, options?: CalendarEventFileUploadOptions) => {
    const formData = new FormData()
    formData.append('file', file)

    if (options?.displayName) {
      formData.append('displayName', options.displayName)
    }
    if (options?.description) {
      formData.append('description', options.description)
    }
    if (options?.tags && options.tags.length > 0) {
      options.tags.forEach((tag) => formData.append('tags', tag))
    }

    return ApiService.upload<CalendarEventMediaUploadResultDto>(
      `/api/events/${eventId}/files/upload`,
      formData
    )
  },

  /**
   * Set cover image for the event
   */
  setCoverImage: (eventId: string, data: SetCalendarEventCoverImageRequest) =>
    ApiService.post<CalendarEventFileSelectionResultDto>(
      `/api/events/${eventId}/files/select`,
      data
    ),

  /**
   * Update file metadata (display name, description, tags)
   */
  updateFile: (eventId: string, fileId: string, data: UpdateCalendarEventMediaFileRequest) =>
    ApiService.patch<CalendarEventMediaFileDto>(
      `/api/events/${eventId}/files/${fileId}`,
      data
    ),

  /**
   * Delete a file from the event
   */
  deleteFile: (eventId: string, fileId: string) =>
    ApiService.delete<void>(`/api/events/${eventId}/files/${fileId}`),

  /**
   * Download file content as blob (proxied through backend to avoid CORS)
   */
  downloadFile: async (eventId: string, fileId: string): Promise<Blob> => {
    const response = await apiClient.get(`/api/events/${eventId}/files/${fileId}/download`, {
      responseType: 'blob',
    })
    return response.data
  },
}
